import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconButton, Tooltip } from '@treezor/design-system';

import { IAttributeConfig, TCheckedValues } from '@/features/wallets/store/models/wallet.model';
import { operationsZones } from '@/store/models/operation.model';
import { selectCurrentProfileWalletByUserId } from '@/store/selectors';
import { useProfileContext } from '@/features/context/ProfileContext';
import { useAppSelector } from '@/hooks/redux-helpers';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import AccordionFilter from '@/ui-kit/table-filters/AccordionFilter';

import { WalletOperationsFormFilters } from './WalletOperationsFormFilters';

import {
  filterAttributestransaltionsKeys,
  getAttributeKeys,
  getCheckedAttributes,
  useAttributes,
  useOperationLabelTranslations,
} from './operationsHelpers';

export interface IOperationsFiltersProps {
  selectedColumns: IAttributeConfig[];
  onFormFiltersChange: (filters?: IAttributeConfig[]) => void;
  onColumnChange: (selectedColumns: IAttributeConfig[]) => void;
}

export const MenuWalletOperationsFilters = ({
  onFormFiltersChange,
  onColumnChange,
  selectedColumns,
}: IOperationsFiltersProps) => {
  const { t } = useTranslation();
  const {
    user: { userId },
  } = useProfileContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dashboardConfig = useDashboardConfig();

  const initialOperationColumns = useAttributes({
    isCrypto: !!dashboardConfig.api.encryption,
    zone: operationsZones.HEADER_TABLE,
    setChecked: false,
  });

  const initialFilters = useAttributes({
    isCrypto: !!dashboardConfig.api.encryption,
    zone: operationsZones.FILTERS,
    setChecked: (attribute) => {
      const initialSelectedItems = initialOperationColumns.map((attr) => attr.name);
      return initialSelectedItems.includes(attribute.name);
    },
  });

  const currentFilters = useAttributes({
    isCrypto: !!dashboardConfig.api.encryption,
    zone: operationsZones.FILTERS,
    setChecked: (attribute) => {
      const selectedItems = selectedColumns.map((attr) => attr.name);
      return selectedItems.includes(attribute.name);
    },
  });

  const keys = getAttributeKeys(currentFilters);
  const operationLabelTranslations = useOperationLabelTranslations(keys, filterAttributestransaltionsKeys);
  const currentWallet = useAppSelector((state) => selectCurrentProfileWalletByUserId(state, userId)?.state?.wallet);

  const handleFiltersChange = useCallback(
    (attributes: IAttributeConfig[]) => {
      const groupKeys = getAttributeKeys(currentFilters, 1);
      const checkedAttributes = getCheckedAttributes(attributes).filter(
        (attribute) => !groupKeys.includes(attribute.name),
      );
      onColumnChange(checkedAttributes);
    },
    [onColumnChange, currentFilters],
  );

  if (!currentWallet) {
    return (
      <div className="w-full text-center py-[0.55rem] typo-body-regular-paragraph text-secondary">
        {t('pages.currentProfiles.wallets.noWallet.available')}
      </div>
    );
  }

  return (
    <div className="flex justify-between items-center w-full">
      <WalletOperationsFormFilters onChangeFilters={onFormFiltersChange} />
      <Tooltip messageContent={t('pages.cores.organizeModalTitle')} placement="left">
        <IconButton
          icon="settings"
          variant="secondary"
          data-testid="organize-transactions-table-btn"
          onClick={() => setIsModalOpen(true)}
        />
      </Tooltip>

      <AccordionFilter<IAttributeConfig & { isChecked: TCheckedValues }>
        initialData={initialFilters}
        title={t('pages.cores.organizeModalTitle')}
        onClose={() => setIsModalOpen(false)}
        translations={operationLabelTranslations}
        onFiltersChange={handleFiltersChange}
        isModalOpen={isModalOpen}
      />
    </div>
  );
};
export default MenuWalletOperationsFilters;
